import { useEffect, useRef } from "react";

/** Сдвиг до «закрыть», px: четверть высоты шторки, но не меньше этого. */
const MIN_DISMISS = 90;
/** Резкий короткий смах тоже закрывает — как в нативных шторках. */
const FLICK_SPEED = 0.6; // px/мс
const FLICK_MIN = 30;
/** Мёртвая зона: дрожь пальца не должна сдвигать шторку. */
const SLOP = 8;

/**
 * Можно ли начинать перетаскивание шторки от этой точки.
 *
 * Нельзя, если палец лёг на прокручиваемый список, который уже промотан:
 * там жест вниз — это возврат к началу списка, а не «закрыть». Считаем не
 * только саму шторку, но и вложенные списки: в мастере раскладки прокрутка
 * живёт внутри `.wiz__list`, и для шторки она была невидима — из-за этого
 * пролистанный список закрывался вместо прокрутки вверх.
 */
export function dragAllowed(onHandle: boolean, scrollTops: number[]): boolean {
  if (onHandle) return true;
  return scrollTops.every((top) => top <= 0);
}

/** То же правило на живом дереве: собирает прокрутки от цели до шторки. */
export function canDragFrom(target: Element | null, root: Element): boolean {
  const onHandle = Boolean(target?.closest(".sheet__grip, .sheet__title"));

  const tops: number[] = [];
  let node: Element | null = target;
  while (node) {
    tops.push(node.scrollTop);
    if (node === root) break;
    node = node.parentElement;
  }
  return dragAllowed(onHandle, tops);
}

/** Закрыть ли шторку, отпущенную на `dy` с последней скоростью `velocity`. */
export function shouldDismiss(dy: number, velocity: number, height: number): boolean {
  if (dy <= 0) return false;
  return dy > Math.max(MIN_DISMISS, height * 0.25) || (velocity > FLICK_SPEED && dy > FLICK_MIN);
}

/**
 * Смахнуть шторку вниз (14.09.2026).
 *
 * «Ручка» сверху обещала жест, которого не было: заказчик тянул — и ничего.
 * Тянуть можно за ручку и заголовок всегда, за содержимое — только когда оно
 * прокручено к началу: иначе жест вниз — это прокрутка списка. Слушатели
 * нативные: touchmove с preventDefault не бывает пассивным, а React вешает
 * пассивные.
 *
 * Возвращает ref для `.sheet__body`; фон берётся соседом из `.sheet`.
 */
export function useSheetDrag<T extends HTMLElement = HTMLDivElement>(onClose: () => void) {
  const body = useRef<T>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const el = body.current;
    if (!el) return;
    const backdrop = el.parentElement?.querySelector<HTMLElement>(".sheet__backdrop") ?? null;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    let dy = 0;
    let armed = false;
    let dragging = false;
    let closing = 0;

    const place = (y: number, animate: boolean) => {
      const transition = animate && !still ? "0.22s cubic-bezier(0.32, 0.72, 0, 1)" : "0s";
      el.style.transition = `transform ${transition}`;
      el.style.transform = y > 0 ? `translate3d(0, ${y}px, 0)` : "";
      if (backdrop) {
        backdrop.style.transition = `opacity ${transition}`;
        backdrop.style.opacity = y > 0 ? String(Math.max(0, 1 - y / (el.offsetHeight || 1))) : "";
      }
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const target = e.target as HTMLElement;
      armed = canDragFrom(target, el);
      dragging = false;
      dy = 0;
      velocity = 0;
      startY = lastY = e.touches[0].clientY;
      lastT = e.timeStamp;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed) return;
      const y = e.touches[0].clientY;
      const delta = y - startY;
      if (!dragging) {
        // Повели вверх — это прокрутка, шторку не трогаем до конца касания.
        if (delta < -SLOP) armed = false;
        if (delta <= SLOP) return;
        dragging = true;
      }
      e.preventDefault();
      const dt = Math.max(1, e.timeStamp - lastT);
      velocity = (y - lastY) / dt;
      lastY = y;
      lastT = e.timeStamp;
      dy = Math.max(0, delta - SLOP);
      place(dy, false);
    };

    const onEnd = () => {
      armed = false;
      if (!dragging) return;
      dragging = false;
      if (shouldDismiss(dy, velocity, el.offsetHeight)) {
        place(el.offsetHeight + 40, true);
        closing = window.setTimeout(() => closeRef.current(), still ? 0 : 200);
      } else {
        place(0, true);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      window.clearTimeout(closing);
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  return body;
}
